import React, { memo } from "react";
import { Typography, Empty } from "antd";
import AnyaIcon from "ac-components/ac-icons/AnyaIcon";
import AnyaChatLoadingIcon from "ac-components/ac-icons/AnyaChatLoadingIcon";
import StreamingText from "views/Dashboards/StreamingText";
import { useFindingDetails } from "./FindingDetailsProvider";

import "views/Dashboards/AnyaSummary.less";
import "./AISummaryTab.less";

/**
 * AISummaryTab - Displays AI-generated summary for current finding
 *
 * Uses the same rendering style as Anya for consistency:
 * - anya-prose class for markdown styling
 * - StreamingText component for details content
 * - reasoningSummary as header, details as main content (matching AnyaMessage.jsx)
 */
const AISummaryTab = memo(function AISummaryTab() {
  const { aiSummaryResponse, aiSummaryLoading, aiSummaryError, aiSummaryExists } =
    useFindingDetails();

  if (aiSummaryLoading) {
    return (
      <div className="ai-summary-tab ai-summary-tab--loading anya-prose">
        <Typography.Text className="system-process">
          Generating AI Summary <AnyaChatLoadingIcon />
        </Typography.Text>
      </div>
    );
  }

  if (aiSummaryError) {
    return (
      <div className="ai-summary-tab ai-summary-tab--error anya-prose">
        <Empty
          description={
            <Typography.Text type="danger">{aiSummaryError}</Typography.Text>
          }
        />
      </div>
    );
  }

  // Summary not yet generated by backend
  if (!aiSummaryExists) {
    return (
      <div className="ai-summary-tab ai-summary-tab--generating anya-prose">
        <AnyaIcon className="font-size-32" />
        <Typography.Text type="secondary" className="m-t-sm">
          AI summary is being generated. Please check back shortly.
        </Typography.Text>
      </div>
    );
  }

  const hasContent = aiSummaryResponse?.reasoningSummary || aiSummaryResponse?.details;

  if (!hasContent) {
    return (
      <div className="ai-summary-tab ai-summary-tab--empty anya-prose">
        <Empty description="No AI summary available" />
      </div>
    );
  }

  return (
    <div className="ai-summary-tab anya-prose">
      <div className="ai-summary-content">
        {aiSummaryResponse?.reasoningSummary && (
          <Typography.Text className="static-message">
            {aiSummaryResponse.reasoningSummary}
          </Typography.Text>
        )}
        {aiSummaryResponse?.details && (
          <StreamingText text={aiSummaryResponse.details} stream={false} />
        )}
      </div>
    </div>
  );
});

export default AISummaryTab;
